import { QuestionIconTooltip, YearSelector } from "../../../../components";
import { Box, Flex, Text, useToast } from "@chakra-ui/react";
import { DataTable } from "../../../../components/DataTable";
import * as React from "react";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useGetSupplierTiersEmissions } from "../../../../hooks/query";
import { showErrorToast } from "../../../../utils";

function SupplierTiers({ selectedYear, setSelectedYear }) {
  const { t } = useTranslation("translation", {
    keyPrefix: "supplierTiers"
  });
  const toast = useToast();
  const [tiersEmissionsData, setTiersEmissionsData] = useState([]);

  const getMappedElements = (data, mappedData) => {
    const sortByTiersNumber = (a, b) => {
      const itemA = a["tiers"];
      const itemB = b["tiers"];

      if (itemA?.length < itemB?.length) return -1;
      if (itemA?.length > itemB?.length) return 1;

      return 0;
    };

    if (data[0].tier !== 1) {
      // Moving suppliers with shorter supply chain to the top to improve the appearance of grid items by showing them stepwise
      data.sort(sortByTiersNumber);
    }

    data.forEach(el => {
      const mappedEl = {
        ownEmissions: el?.supplier?.ownEmissions ?? "",
        apportionedEmissions: el?.supplier?.apportionedEmissions ?? "",
        turnoverPercentage: el?.supplier?.companyPercentage ?? "",
        totalEmissions: el?.supplier?.totalEmissions
      };
      mappedEl[`tier${el?.tier}Supplier`] = el?.supplier?.name ?? "";

      if (el?.tier !== 1) {
        // Adding specific background colour to suppliers with tier lower than 1, in order to visually group them
        mappedEl["rowCustomStyles"] = {
          backgroundColor: "#f1f1f1"
        };
      }

      mappedData.push(mappedEl);

      if (el?.tiers && el?.tiers?.length > 0) {
        getMappedElements(el.tiers, mappedData);
      }
    });
  };

  const {
    isFetching: isTiersEmissionsDataFetching
  } = useGetSupplierTiersEmissions({
    year: selectedYear
  }, {
    onSuccess: (data) => {
      const mappedData = [];

      if (data && data.length > 0) {
        const sortAlphabeticallyBySupplierName = (a, b) => {
          return a.supplier?.name?.toLowerCase().localeCompare(b.supplier?.name?.toLowerCase());
        };

        data.sort(sortAlphabeticallyBySupplierName);
        getMappedElements(data, mappedData);
      }

      setTiersEmissionsData(mappedData);
    },
    onError: (err) => showErrorToast(toast, err.response?.data?.parameters?.message ?? err.message),
    cacheTime: 0
  });

  const columns = useMemo(
    () => {
      return [
        {
          header: t("gridHeaders.tierSupplierName", { tierNumber: 1 }),
          accessorKey: "tier1Supplier",
          size: 100,
          customStyles: {
            "whiteSpace": "pre-line"
          }
        },
        {
          header: t("gridHeaders.tierSupplierName", { tierNumber: 2 }),
          accessorKey: "tier2Supplier",
          size: 100,
          customStyles: {
            "whiteSpace": "pre-line"
          }
        },
        {
          header: t("gridHeaders.tierSupplierName", { tierNumber: 3 }),
          accessorKey: "tier3Supplier",
          size: 100,
          customStyles: {
            "whiteSpace": "pre-line"
          }
        },
        {
          header: t("gridHeaders.tierSupplierName", { tierNumber: 4 }),
          accessorKey: "tier4Supplier",
          size: 100,
          customStyles: {
            "whiteSpace": "pre-line"
          }
        },
        {
          header: <Flex alignItems="center">
            {t("gridHeaders.ownEmissions")}
            <QuestionIconTooltip
              label={t("ownEmissionsTooltip")}
              ariaLabel={"own-emissions-tooltip"}
              height={"unset"}
            />
          </Flex>,
          accessorKey: "ownEmissions",
          size: 110,
          customStyles: {
            "whiteSpace": "pre-line"
          }
        },
        {
          header: t("gridHeaders.apportionedEmissions"),
          accessorKey: "apportionedEmissions",
          size: 110,
          customStyles: {
            "whiteSpace": "pre-line"
          }
        },
        {
          header: t("gridHeaders.turnoverPercentage"),
          size: 110,
          customStyles: {
            "whiteSpace": "pre-line"
          },
          cell: (el) => (
            <Text>
              {el.row?.original?.turnoverPercentage ? `${el.row?.original?.turnoverPercentage}%` : ""}
            </Text>
          )
        },
        {
          header: t("gridHeaders.totalEmissions"),
          accessorKey: "totalEmissions",
          size: 110,
          customStyles: {
            "whiteSpace": "pre-line"
          }
        }
      ];
    },
    [t]
  );

  return (
    <Box>
      <YearSelector selectedYear={selectedYear} handleYearSelection={(value) => setSelectedYear(value)} />
      <DataTable
        data={tiersEmissionsData}
        columns={columns}
        isLoading={isTiersEmissionsDataFetching}
      />
    </Box>
  );
}

export default SupplierTiers;